import type { MessageTrigger, TriggerNotification } from '../types/triggers';
import { SESSION_USER_KEY } from '../config';
import { getGlobal } from '../global';

const STORAGE_KEYS = {
  TRIGGERS: 'telegram_triggers',
  NOTIFICATIONS: 'trigger_notifications',
};

let currentUserId: string | undefined;
const triggerUpdateCallbacks: Array<() => void> = [];

// Type for a trigger match
export interface TriggerMatch {
  id: string;
  chatId: string;
  triggerId: string;
  messageId: number;
  messageText: string;
  matchedText: string;
  createdAt: number;
}

const STORAGE_KEY_TRIGGER_MATCHES = 'trigger_matches';

function getItem<T>(key: string, defaultValue: T): T {
  const item = localStorage.getItem(key);
  if (!item) return defaultValue;
  try {
    return JSON.parse(item);
  } catch {
    return defaultValue;
  }
}

function setItem<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function setCurrentUserId(userId: string) {
  currentUserId = userId;
  // Don't modify the session storage here, it's handled by the session manager
}

export function getCurrentUserId(): string | undefined {
  return currentUserId;
}

export function getTriggers(): MessageTrigger[] {
  return getItem<MessageTrigger[]>(STORAGE_KEYS.TRIGGERS, []);
}

export function setTriggers(triggers: MessageTrigger[]): void {
  setItem(STORAGE_KEYS.TRIGGERS, triggers);
  triggerUpdateCallbacks.forEach(callback => callback());
}

export function addTriggerToChat(chatId: string, trigger: Omit<MessageTrigger, 'id' | 'createdAt' | 'notificationCount'>): MessageTrigger {
  const triggers = getTriggers();
  const newTrigger: MessageTrigger = {
    ...trigger,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
    notificationCount: 0,
  };
  const updatedTriggers = [...triggers, newTrigger];
  setTriggers(updatedTriggers);
  window.dispatchEvent(new CustomEvent('triggersUpdated', { detail: { chatId } }));
  return newTrigger;
}

export function updateTriggerInChat(chatId: string, triggerId: string, updates: Partial<MessageTrigger>): MessageTrigger | null {
  const triggers = getTriggers();
  const updatedTriggers = triggers.map(trigger =>
    trigger.id === triggerId ? { ...trigger, ...updates } : trigger
  );
  setTriggers(updatedTriggers);
  const updatedTrigger = updatedTriggers.find(t => t.id === triggerId) || null;
  window.dispatchEvent(new CustomEvent('triggersUpdated', { detail: { chatId } }));
  return updatedTrigger;
}

export function deleteTriggerFromChat(chatId: string, triggerId: string): boolean {
  const triggers = getTriggers();
  const updatedTriggers = triggers.filter(trigger => trigger.id !== triggerId);
  setTriggers(updatedTriggers);
  window.dispatchEvent(new CustomEvent('triggersUpdated', { detail: { chatId } }));
  return true;
}

export function onTriggerUpdate(callback: () => void): () => void {
  triggerUpdateCallbacks.push(callback);
  return () => {
    const index = triggerUpdateCallbacks.indexOf(callback);
    if (index !== -1) {
      triggerUpdateCallbacks.splice(index, 1);
    }
  };
}

export async function getAllNotifications(): Promise<TriggerNotification[]> {
  const currentUserId = getGlobal().currentUserId;
  if (!currentUserId) {
    console.warn('currentUserId is missing in getAllNotifications');
    return [];
  }

  const triggers = getTriggers();
  return triggers
    .filter(trigger => trigger.notificationCount > 0)
    .map(trigger => ({
      id: trigger.id,
      triggerId: trigger.id,
      chatId: trigger.chatId,
      messageId: 0, // This will need to be updated when we implement message tracking
      messageText: trigger.substring,
      matchedText: trigger.substring,
      createdAt: trigger.lastTriggeredAt || Date.now(),
      isRead: false, // This will need to be updated when we implement read status
    }));
}

export function getTriggersForChat(chatId: string): MessageTrigger[] {
  return getTriggers().filter(trigger => trigger.chatId === chatId);
}

export function addTrigger(trigger: MessageTrigger): void {
  const triggers = getTriggers();
  setTriggers([...triggers, trigger]);
}

export function updateTrigger(triggerId: string, updates: Partial<MessageTrigger>): void {
  const triggers = getTriggers();
  const updatedTriggers = triggers.map(trigger =>
    trigger.id === triggerId ? { ...trigger, ...updates } : trigger
  );
  setTriggers(updatedTriggers);
}

export function deleteTrigger(triggerId: string): void {
  const triggers = getTriggers();
  setTriggers(triggers.filter(trigger => trigger.id !== triggerId));
}

export function getAllTriggers(): MessageTrigger[] {
  return getTriggers();
}

export function createTrigger(trigger: Omit<MessageTrigger, 'id' | 'createdAt' | 'notificationCount'>): MessageTrigger {
  return addTrigger(trigger);
}

export function editTrigger(triggerId: string, updates: Partial<MessageTrigger>): MessageTrigger | null {
  return updateTrigger(triggerId, updates);
}

export function removeTrigger(triggerId: string): boolean {
  return deleteTrigger(triggerId);
}

export async function getNotificationsForChat(chatId: string): Promise<TriggerNotification[]> {
  const currentUserId = getGlobal().currentUserId;
  if (!currentUserId) {
    console.warn('currentUserId is missing in getNotificationsForChat');
    return [];
  }

  const triggers = getTriggers();
  return triggers
    .filter(trigger => trigger.chatId === chatId && trigger.notificationCount > 0)
    .map(trigger => ({
      id: trigger.id,
      triggerId: trigger.id,
      chatId: trigger.chatId,
      messageId: 0, // This will need to be updated when we implement message tracking
      messageText: trigger.substring,
      matchedText: trigger.substring,
      createdAt: trigger.lastTriggeredAt || Date.now(),
      isRead: false, // This will need to be updated when we implement read status
    }));
}

function getTriggerMatches(): TriggerMatch[] {
  return getItem<TriggerMatch[]>(STORAGE_KEY_TRIGGER_MATCHES, []);
}

export function addTriggerMatch(match: Omit<TriggerMatch, 'id' | 'createdAt'>) {
  const matches = getTriggerMatches();
  const newMatch: TriggerMatch = {
    ...match,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
  };
  const updatedMatches = [...matches, newMatch];
  setItem(STORAGE_KEY_TRIGGER_MATCHES, updatedMatches);
  // Debug log
  console.debug('[TriggerManager] addTriggerMatch:', newMatch, updatedMatches);
}

export function getTriggerMatchesForTrigger(triggerId: string): TriggerMatch[] {
  const allMatches = getTriggerMatches();
  const filtered = allMatches.filter(m => m.triggerId === triggerId);
  // Debug log
  console.debug('[TriggerManager] getTriggerMatchesForTrigger:', triggerId, filtered);
  return filtered;
}

export async function checkMessageForTriggers(chatId: string, messageId: number, messageText: string): Promise<void> {
  const currentUserId = getGlobal().currentUserId;
  if (!currentUserId) {
    console.warn('currentUserId is missing in checkMessageForTriggers');
    return;
  }
  
  const triggers = getTriggers();
  const matchingTriggers = triggers.filter(trigger => 
    trigger.isEnabled && 
    trigger.chatId === chatId && 
    (trigger.isCaseSensitive 
      ? messageText.includes(trigger.substring)
      : messageText.toLowerCase().includes(trigger.substring.toLowerCase()))
  );

  if (matchingTriggers.length === 0) return;

  // Update each matching trigger and save match info
  for (const trigger of matchingTriggers) {
    const updatedTrigger = {
      ...trigger,
      notificationCount: (trigger.notificationCount || 0) + 1,
      lastTriggeredAt: Date.now(),
    };
    updateTriggerInChat(chatId, trigger.id, updatedTrigger);
    // Save match info
    addTriggerMatch({
      chatId,
      triggerId: trigger.id,
      messageId,
      messageText,
      matchedText: trigger.substring,
    });
  }

  // Notify about trigger updates
  window.dispatchEvent(new CustomEvent('triggersUpdated', { detail: { chatId } }));
} 